import { Minus, Plus, TrendingUp } from 'lucide-react';
import { formatGBP, formatGBPfromPence, calculateOrderTotalPence, EVEN_TICKET_PRICE_PENCE } from '../../lib/format';
import { useHaptic } from '../../hooks/useHaptic';

// Quick-picks. The RECOMMENDED_QTY (2) is highlighted as the featured
// pair-price entry point. Users can still pick any other quantity; totals
// recompute via the same odd/even formula in either direction.
const QUICK = [1, 2, 5, 10, 25];
const RECOMMENDED_QTY = 2;

/**
 * Compact, tactile ticket selector.
 *
 * Design goals (post-user-feedback):
 *  - Single self-contained card, not a stack of banners.
 *  - Stepper is finger-friendly (44px targets) but never bulky.
 *  - Live per-ticket average sits *inside* the stepper so it updates in
 *    place as the user taps — no separate "results" block to scan.
 *  - Quick-pick pills are compact (single-line labels + tiny price sub).
 *  - Even/odd messaging collapses to ONE slim line (saving OR one-tap
 *    upgrade nudge), not two competing cards.
 *  - Inline checkout is slim; the sticky bottom bar is still the primary
 *    conversion surface on scroll.
 *
 * All pricing calculations are unchanged: 1st ticket at `price`, every 2nd
 * at £1 flat via the shared `calculateOrderTotalPence` helper.
 */
export const TicketSelector = ({ price, quantity, setQuantity, maxQty = 100, onCheckout }) => {
  const haptic = useHaptic();
  // Only vibrate when the quantity actually changes (i.e. not when hitting
  // min/max clamps) so a locked +/- button never buzzes without effect.
  const set = (v) => {
    const next = Math.max(1, Math.min(maxQty, v));
    if (next !== quantity) haptic('light');
    setQuantity(next);
  };
  const totalPence = calculateOrderTotalPence(price, quantity);
  const perTicketPence = quantity > 0 ? Math.round(totalPence / quantity) : 0;
  const evenPriceGbp = EVEN_TICKET_PRICE_PENCE / 100;

  // Savings + nudge messaging (unchanged semantics, one line only).
  const flatBasePence = Math.round((price || 0) * 100) * quantity;
  const savingsPence = flatBasePence - totalPence;
  const nextEvenTotalPence = calculateOrderTotalPence(price, quantity + 1);
  const nextEvenPerTicketPence = Math.round(nextEvenTotalPence / (quantity + 1));
  const isEven = quantity > 0 && quantity % 2 === 0;
  const isOddNudge = quantity > 1 && quantity % 2 === 1;
  const atMin = quantity <= 1;
  const atMax = quantity >= maxQty;

  return (
    <div
      className="comp-card border border-[color:var(--pd-accent-yellow)]/45 rounded-2xl p-4 md:p-5"
      data-testid="ticket-selector"
    >
      {/* Header row — title + pricing formula on the same line. Replaces
          the old chunky "2 tickets for £X" banner. */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="font-mono text-[11px] text-[color:var(--pd-accent-yellow)] uppercase tracking-widest">
          Choose tickets
        </div>
        <div className="font-mono text-[10px] text-muted-sage" data-testid="ticket-pair-price">
          <span className="text-[color:var(--pd-text)]">{formatGBP(price)}</span>
          <span> · every 2nd {formatGBP(evenPriceGbp)}</span>
        </div>
      </div>

      {/* Unified stepper — quantity + live totals sit *inside* the control
          so the eye doesn't have to jump around when tapping +/−. */}
      <div className="mt-3 flex items-stretch rounded-xl border border-pd overflow-hidden bg-[color:var(--pd-bg)]/60">
        <button
          type="button"
          onClick={() => set(quantity - 1)}
          disabled={atMin}
          className="w-12 md:w-14 flex items-center justify-center text-[color:var(--pd-text)] disabled:text-muted-sage disabled:opacity-40 hover:bg-[color:var(--pd-accent-yellow)]/10 border-r border-pd transition-colors"
          aria-label="Decrease quantity"
          data-testid="ticket-qty-decrease-button"
        >
          <Minus size={18} />
        </button>

        <div className="flex-1 py-2.5 md:py-3 px-3 flex items-center justify-center gap-3 text-center">
          <div
            className="font-display text-3xl md:text-4xl leading-none text-[color:var(--pd-text)]"
            data-testid="ticket-qty-value"
          >
            {quantity}
          </div>
          <div className="text-left leading-tight">
            <div className="font-mono text-[9px] text-muted-sage uppercase tracking-widest">
              {quantity === 1 ? 'ticket' : 'tickets'}
            </div>
            {quantity >= 2 ? (
              <div className="font-mono text-[10px] text-muted-sage mt-0.5" data-testid="ticket-avg-per">
                {formatGBPfromPence(perTicketPence)}/ea
              </div>
            ) : (
              <div className="font-mono text-[10px] text-muted-sage mt-0.5">first ticket</div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={() => set(quantity + 1)}
          disabled={atMax}
          className="w-12 md:w-14 flex items-center justify-center text-[color:var(--pd-text)] disabled:text-muted-sage disabled:opacity-40 hover:bg-[color:var(--pd-accent-yellow)]/10 border-l border-pd transition-colors"
          aria-label="Increase quantity"
          data-testid="ticket-qty-increase-button"
        >
          <Plus size={18} />
        </button>
      </div>

      {/* Quick-pick pills — fixed 5-column grid so every pill is fully
          visible on mobile without horizontal scroll. */}
      <div
        className="mt-3 grid grid-cols-5 gap-1.5 md:gap-2"
        role="group"
        aria-label="Common quantities"
      >
        {QUICK.map((q) => {
          const active = quantity === q;
          const recommended = q === RECOMMENDED_QTY;
          const qTotal = calculateOrderTotalPence(price, q);
          return (
            <button
              key={q}
              type="button"
              onClick={() => set(q)}
              className={`relative px-1 py-2 rounded-lg text-xs border transition-colors ${
                active
                  ? 'bg-[color:var(--pd-accent-yellow)] border-[color:var(--pd-accent-yellow)] text-[#14251c]'
                  : recommended
                  ? 'bg-transparent border-[color:var(--pd-accent-yellow)]/50 text-[color:var(--pd-text)] hover:bg-[color:var(--pd-accent-yellow)]/10'
                  : 'bg-transparent border-pd text-muted-sage hover:text-[color:var(--pd-text)] hover:border-[color:var(--pd-text)]/40'
              }`}
              data-testid={`ticket-qty-quick-pick-${q}`}
            >
              {recommended && !active ? (
                <span
                  className="absolute -top-1.5 left-1/2 -translate-x-1/2 bg-[color:var(--pd-gold)] text-[#14251c] text-[8px] font-mono uppercase tracking-widest px-1 py-px rounded"
                >
                  Best
                </span>
              ) : null}
              <div className="font-semibold leading-none">{q}</div>
              <div className={`font-mono text-[10px] mt-1 leading-none truncate ${active ? 'text-[#14251c]/80' : 'text-muted-sage'}`}>
                {formatGBPfromPence(qTotal)}
              </div>
            </button>
          );
        })}
      </div>

      {/* Slim total + saving/nudge row — replaces the old huge £-display
          block AND the separate "Best value / You're saving" card. One
          unified line keeps the flow calm. */}
      <div className="mt-4 pt-3 border-t border-[color:var(--pd-border)]">
        <div className="flex items-baseline justify-between gap-3">
          <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest">Total</div>
          <div
            className="font-display text-2xl md:text-3xl leading-none text-[color:var(--pd-accent-yellow)]"
            data-testid="ticket-total-price"
          >
            {formatGBPfromPence(totalPence)}
          </div>
        </div>

        {isEven && savingsPence > 0 ? (
          <div
            className="mt-2 flex items-center gap-2 text-[11px] md:text-xs text-[color:var(--pd-gold)]"
            data-testid="ticket-savings-callout"
          >
            <TrendingUp size={13} className="shrink-0" />
            <span>
              <span className="font-mono uppercase tracking-widest text-[9px] md:text-[10px] mr-1">Best value</span>
              You&rsquo;re saving <span className="font-mono">{formatGBPfromPence(savingsPence)}</span>.
            </span>
          </div>
        ) : isOddNudge ? (
          <div
            className="mt-2 flex items-center justify-between gap-2"
            data-testid="ticket-nudge-callout"
          >
            <div className="flex items-center gap-2 text-[11px] md:text-xs text-[color:var(--pd-gold)] min-w-0">
              <TrendingUp size={13} className="shrink-0" />
              <span className="truncate">
                +1 ticket = <span className="font-mono">{formatGBPfromPence(nextEvenPerTicketPence)}/ea</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => set(quantity + 1)}
              className="shrink-0 border border-[color:var(--pd-gold)]/60 text-[color:var(--pd-gold)] font-mono text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-md hover:bg-[color:var(--pd-gold)]/10"
              data-testid="ticket-nudge-plus-one"
            >
              +1 for {formatGBP(evenPriceGbp)}
            </button>
          </div>
        ) : (
          <div
            className="mt-2 text-[11px] text-muted-sage"
            data-testid="ticket-price-breakdown"
          >
            Adding a 2nd ticket is just {formatGBP(evenPriceGbp)} more.
          </div>
        )}
      </div>

      {/* Slim inline checkout — matches the sticky bar's aesthetic so the
          two surfaces read as the same control at different scroll depths. */}
      {onCheckout ? (
        <button
          type="button"
          onClick={onCheckout}
          className="mt-4 w-full bg-[color:var(--pd-accent-yellow)] text-[#14251c] px-4 py-3 rounded-xl text-sm md:text-base font-medium hover:brightness-95 transition-[filter]"
          data-testid="ticket-selector-checkout"
        >
          Checkout &mdash; {formatGBPfromPence(totalPence)}
        </button>
      ) : null}
    </div>
  );
};

export default TicketSelector;
